import React from "react";
import { motion } from "framer-motion";
import { moodStyleFor } from "../chat/MoodIndicator";

/**
 * Renders emoji-based ambient particles based on world theme + mood.
 * Fully CSS/DOM, no external assets.
 */
const THEME_PARTICLES = {
  meadow: { chars: ["🌸", "🌿"], count: 6, drift: "sway" },
  midnight: { chars: ["✨", "⭐"], count: 8, drift: "twinkle" },
  sunset: { chars: ["🌤", "🍃"], count: 5, drift: "sway" },
  ocean: { chars: ["🫧", "🐚"], count: 6, drift: "float" },
  arcade: { chars: ["◆", "▲", "●"], count: 10, drift: "twinkle" },
  cafe: { chars: ["☕", "💨"], count: 4, drift: "float" },
  space: { chars: ["✦", "·", "★"], count: 12, drift: "twinkle" },
  forest: { chars: ["🍃", "🌱"], count: 5, drift: "sway" },
};

const MOOD_PARTICLES = {
  happy:    { chars: ["🎉", "✨"], count: 4, drift: "confetti" },
  romantic: { chars: ["💗", "💕"], count: 5, drift: "float" },
  playful:  { chars: ["⭐", "🌈"], count: 4, drift: "twinkle" },
  tense:    { chars: [], count: 0, drift: "" },
  sad:      { chars: ["💧"], count: 4, drift: "rain" },
  urgent:   { chars: ["⚡"], count: 3, drift: "twinkle" },
  neutral:  { chars: [], count: 0, drift: "" },
};

function randPos(seed) {
  const x = ((Math.sin(seed * 12.34) + 1) / 2) * 100;
  // Keep particles in the middle-to-bottom of the visible area (15%–90%)
  // so they never overlap sticky UI at the very top.
  const y = 15 + ((Math.cos(seed * 78.91) + 1) / 2) * 75;
  const d = ((Math.sin(seed * 3.14) + 1) / 2) * 6 + 4;
  const s = ((Math.cos(seed * 1.61) + 1) / 2) * 10 + 12;
  return { x, y, d, s };
}

function Particle({ i, ch, drift }) {
  const p = randPos(i * 3.7 + ch.charCodeAt(0));
  const anims = {
    sway: { y: [0, -8, 0], x: [0, 4, 0] },
    twinkle: { opacity: [0.2, 1, 0.3], scale: [1, 1.1, 1] },
    float: { y: [0, -14, 0] },
    confetti: { y: [-20, 40, 100], rotate: [0, 90, 180, 270, 360], opacity: [0, 1, 1, 0] },
    rain: { y: ["-10%", "110%"], opacity: [0, 0.9, 0] },
  };
  const anim = anims[drift] || {};
  return (
    <motion.span
      className="absolute pointer-events-none select-none"
      style={{ left: `${p.x}%`, top: `${p.y}%`, fontSize: p.s, filter: "drop-shadow(1px 1px 0 rgba(0,0,0,0.15))" }}
      animate={anim}
      transition={{ duration: p.d, repeat: Infinity, ease: "easeInOut", delay: (i * 0.13) % 2 }}
    >
      {ch}
    </motion.span>
  );
}

export default function AtmosphereLayer({ theme, mood, enabled = true }) {
  if (!enabled) return null;
  const tCfg = THEME_PARTICLES[theme] || null;
  const mCfg = MOOD_PARTICLES[mood] || null;
  const all = [];
  if (tCfg) {
    for (let i = 0; i < tCfg.count; i++) {
      const ch = tCfg.chars[i % tCfg.chars.length];
      all.push({ ch, drift: tCfg.drift, key: `t-${i}` });
    }
  }
  if (mCfg) {
    for (let i = 0; i < mCfg.count; i++) {
      const ch = mCfg.chars[i % mCfg.chars.length];
      all.push({ ch, drift: mCfg.drift, key: `m-${i}` });
    }
  }
  if (!all.length) return null;
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" data-testid="atmosphere-layer" aria-hidden>
      {all.map((it, i) => <Particle key={it.key} i={i + 1} ch={it.ch} drift={it.drift} />)}
    </div>
  );
}
